import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Select } from "../../../../form/select";
import { Main } from "./style";
import { Textarea } from "../../../../form/textarea";
import { getGoogleAccountToken } from "../../../../auth/method";
import { AuthenticateSelect } from "../../../../auth/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import { ExpandedBlock } from "../../../expanded";
import { Styled } from "./style";
import { RootState } from "../../../../../redux/store";
import { useSelector } from "react-redux";
import { getEffectiveGlobalSpreadsheet, matchSheetFromList, nodeUsesGlobalSpreadsheet, resolveDefaultSheetName } from "../../../../run/globalConnections/spreadsheetUtils";

type SpreadsheetProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Spreadsheet({ id, data, setChildMinimize, setChildSettings }: SpreadsheetProps) {
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const globalSpreadsheet = useSelector((state: RootState) => state.project.globalGoogleSpreadsheet);
   const activeGlobalSpreadsheet = getEffectiveGlobalSpreadsheet(data.value, globalSpreadsheet);
   const usesGlobalSpreadsheet = nodeUsesGlobalSpreadsheet(data.value, activeGlobalSpreadsheet);
   const effectiveSpreadsheetId = usesGlobalSpreadsheet
      ? activeGlobalSpreadsheet?.spreadsheet_id
      : data.value?.spreadsheet_id;
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Create Spreadsheet', refId: 'create_spreadsheet', isActive: true }, 
      { id: 2, name: 'Delete Spreadsheet', refId: 'delete_spreadsheet' },
      { id: 3, name: 'Append or update row', refId: 'upsert_row_value' },
      { id: 4, name: 'Append Row', refId: 'append_row' },
      { id: 5, name: 'Append Multi Row', refId: 'append_multi_row' },
      { id: 6, name: 'Update Row', refId: 'update_row' },
      { id: 7, name: 'Update Multi Row', refId: 'update_multi_row' },
      { id: 8, name: 'Clear Sheet', refId: 'clear_sheet' },
      { id: 9, name: 'Create Sheet', refId: 'create_sheet' },
      { id: 10, name: 'Delete Sheet', refId: 'delete_sheet' },
      { id: 11, name: 'Delete columns from sheet', refId: 'delete_columns' },
      { id: 12, name: 'Delete rows from sheet', refId: 'delete_rows' },
      { id: 13, name: 'Get column(s) in sheet', refId: 'get_column' },
      { id: 14, name: 'Get row(s) in sheet', refId: 'get_row' },
      { id: 15, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [isCanFetch, setIsCanFetch] = React.useState(false);
   const [sheets, setSheets] = React.useState<selectDataType[]>([]);
   const [isLoadingSheets, setIsLoadingSheets] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { request: requests[0].refId, sheet_name: 'Sheet1', any: '' });
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // --
   React.useEffect(() => {
      if (!isCanFetch && isWorkflowRun && data.value?.request === 'delete_spreadsheet') {
         setIsCanFetch(true);
      } else if (isCanFetch && !isWorkflowRun) {
         handleActions('fetch');
         setIsCanFetch(false);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isCanFetch, isWorkflowRun, data.value?.request]);

   // Fetch the spreadsheet list whenever a connection is present but no files have been
   // loaded yet. Not gated on the node being expanded: a connection can be assigned from
   // the Global Connections popup while the node is collapsed, and it must fully
   // initialize immediately — the same as if the user had opened the node manually and
   // clicked "Fetch Documents".
   React.useEffect(() => {
      if (data.value?.connection && data.value?.request !== 'create_spreadsheet' && !data.value?.files?.length && !data.value?.isLoading) {
         handleActions('fetch');
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.connection]);

   // Fetch the list of sheet tabs whenever a document is selected (and whenever the
   // document changes, since tabs differ per spreadsheet). Runs on mount too, so a
   // previously-saved node re-populates the dropdown without needing to reopen it.
   React.useEffect(() => {
      if (data.value?.connection && effectiveSpreadsheetId) {
         fetchSheets(effectiveSpreadsheetId);
      } else {
         setSheets([]);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [effectiveSpreadsheetId, data.value?.connection]);

   // Auto-apply the project's global document unless this node was explicitly overridden.
   React.useEffect(() => {
      if (!activeGlobalSpreadsheet?.spreadsheet_id) return;
      if (data.value?.isGlobalSpreadsheetId === false) return;

      const keepUserSheet =
         data.value?.sheetNameUserOverride === true &&
         String(data.value?.sheet_name || '').trim();
      const sheet_name = keepUserSheet
         ? String(data.value.sheet_name).trim()
         : resolveDefaultSheetName({ data });
      const patch: Record<string, any> = {
         spreadsheet_id: activeGlobalSpreadsheet.spreadsheet_id,
         isGlobalSpreadsheetId: true,
         globalSpreadsheetName: activeGlobalSpreadsheet.spreadsheet_name || '',
         files: [{
            id: activeGlobalSpreadsheet.spreadsheet_id,
            name: activeGlobalSpreadsheet.spreadsheet_name || activeGlobalSpreadsheet.spreadsheet_id,
         }],
      };
      if (sheet_name && sheet_name !== data.value?.sheet_name) patch.sheet_name = sheet_name;

      const alreadySynced =
         data.value?.isGlobalSpreadsheetId === true &&
         data.value?.spreadsheet_id === activeGlobalSpreadsheet.spreadsheet_id &&
         data.value?.globalSpreadsheetName === (activeGlobalSpreadsheet.spreadsheet_name || '') &&
         !patch.sheet_name;
      if (alreadySynced) return;

      updateNodeValue(id, setNodes, patch);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeGlobalSpreadsheet?.spreadsheet_id, activeGlobalSpreadsheet?.spreadsheet_name, data.value?.isGlobalSpreadsheetId]);

   // ---
   const fetchSheets = async (spreadsheetId: string) => {
      setIsLoadingSheets(true);
      try {
         const token = await getGoogleAccountToken(data.value?.connection, id, setNodes);
         const response = (await axios.get(`/project_management/spreadsheet/sheets?token=${token}&spreadsheet_id=${spreadsheetId}`)).data;
         const rawNames: string[] = (response.sheets || []).map((sheet: any) => String(sheet.name || '')).filter(Boolean);
         const preferred = data.value?.sheetNameUserOverride
            ? String(data.value?.sheet_name || '').trim() || resolveDefaultSheetName({ data })
            : resolveDefaultSheetName({ data }) || String(data.value?.sheet_name || '').trim();
         const matched = matchSheetFromList(preferred, rawNames) || (preferred ? '' : rawNames[0] || '');
         const activeSheetName = matched || preferred;
         const sheetsList = rawNames.map((name, index) => ({
            id: index + 1,
            name,
            refId: name,
            isActive: name === activeSheetName,
         }));
         setSheets(sheetsList);
         // Prefer the template/intended tab (or fuzzy match). Only fall back to first tab if nothing matches.
         if (matched && matched !== data.value?.sheet_name) {
            updateNodeValue(id, setNodes, { sheet_name: matched });
         } else if (!matched && sheetsList.length > 0 && !data.value?.sheet_name) {
            updateNodeValue(id, setNodes, { sheet_name: sheetsList[0].refId });
         }
      } catch {
         setSheets([]);
      }
      setIsLoadingSheets(false);
   }

   // ---
   const handleSelectDoc = (selectId: number) => {
      const selected = (data.value?.files || []).map((file: selectDataType, index: number) => ({ id: index + 1, refId: file.id, name: file.name }))
         .find((f: selectDataType) => f.id === selectId);
      if (!selected) return;
      setSheets([]);
      // Clear sheet_name so fetchSheets re-applies intendedSheet / name hints for this node.
      updateNodeValue(id, setNodes, {
         spreadsheet_id: selected.refId,
         sheet_name: '',
         isGlobalSpreadsheetId: false,
         sheetNameUserOverride: false,
      });
   }

   const handleOverrideDocument = () => {
      updateNodeValue(id, setNodes, { isGlobalSpreadsheetId: false });
   };

   // ---
   const handleSelectSheet = (selectId: number) => {
      const selected = sheets.find(s => s.id === selectId);
      if (!selected) return;
      setSheets(sheets => sheets.map(s => ({ ...s, isActive: s.id === selectId })));
      // User picked a tab manually — keep it even if Connections re-applies the global document.
      updateNodeValue(id, setNodes, { sheet_name: selected.refId, sheetNameUserOverride: true });
   }

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: data, connectionId: data?.id || data?._id || null });
         // Pass the freshly-selected connection directly — `data.value?.connection`
         // (the component's own prop) is still the stale pre-selection value at this
         // point in the render cycle, since the updateNodeValue above hasn't re-rendered
         // yet. Reading it here would silently fetch with no/old connection.
         await handleActions('fetch', undefined, data);
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   }

   // ---
   const handleActions = async (type: 'fetch' | 'remove' | 'request' | 'header', info?: any, connectionOverride?: any) => {
      if (type === 'fetch') {
         try {
            updateNodeValue(id, setNodes, { isLoading: true });
            const token = await getGoogleAccountToken(connectionOverride || data.value?.connection, id, setNodes);
            const response = (await axios.get(`/project_management/spreadsheet/user_spreadsheets?token=${token}`)).data;
            updateNodeValue(id, setNodes, { files: response.spreadsheets, isLoading: false });
         } catch {
            updateNodeValue(id, setNodes, { isLoading: false });
         }
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { files: null, spreadsheet_id: '' });

      } else if (type === 'request') {
         const refId = requests.find(obj => obj.id === info)?.refId;
         setRequests((requests) => requests.map((request) => {
            if (request.id === info) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));

         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { 
                  ...node.data.value, request: refId, any: '', end_range: '', area: '', search_value: '' 
               }}}
            } else return node;
         }));
      }
   }

   // ---
   const isFilesEdit = data.value?.request !== 'create_spreadsheet' && (usesGlobalSpreadsheet || data.value?.files?.length);
   const isWarning = !data.value?.connection;
   const documentSelectData = usesGlobalSpreadsheet && activeGlobalSpreadsheet?.spreadsheet_id
      ? [{
         id: 1,
         name: activeGlobalSpreadsheet.spreadsheet_name || data.value?.globalSpreadsheetName || 'Global document',
         refId: activeGlobalSpreadsheet.spreadsheet_id,
         isActive: true,
      }]
      : (data.value?.files || []).map((file: selectDataType, index: number) => ({
         id: index + 1, name: file.name, refId: file.id, isActive: file.id === data.value?.spreadsheet_id
      }));

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null }
         { isExpanded ? 
         <ExpandedBlock
            data={{ id, ...data }}
            handleClose={ () => setIsExpanded(false) }
            isWarning={ isWarning }
            style={{ minWidth: '420px' }}
         ><Styled>
            <AuthenticateSelect
               type="google"
               defaultId={ data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id || '' }
               defaultConnection={ data.value?.connection }
               handleAuthenticate={ handleAuthenticate }
               background={ data.background }
            />
            <div className="action">
               { data.value?.connection && data.value?.request !== 'create_spreadsheet' ? <>
                  <button className="btn1 withBorder" onClick={ _=> handleActions('fetch') } disabled={ data.value?.isLoading }>
                     <FontAwesomeIcon icon={ faArrowRotateRight } spin={ data.value?.isLoading } /> { data.value?.isLoading ? 'Fetching...' : 'Fetch Documents' }
                  </button>
                  { isFilesEdit && !usesGlobalSpreadsheet ? <button className="btn1 withBorder delete" onClick={ _=> handleActions('remove') } disabled={ data.value?.isLoading }>Revert Documents</button> : null }
               </>: null }
            </div>
            { usesGlobalSpreadsheet ? (
               <div className="globalDocBadge">
                  <span>
                     Using global document
                     { (activeGlobalSpreadsheet?.spreadsheet_name || data.value?.globalSpreadsheetName)
                        ? `: ${activeGlobalSpreadsheet?.spreadsheet_name || data.value?.globalSpreadsheetName}`
                        : '' }
                  </span>
                  <button type="button" className="linkBtn" onClick={handleOverrideDocument}>Override</button>
               </div>
            ) : null }
            <Select title="Request" data={ requests } handleSelectItem={ (id) => handleActions('request', id) } isSmall={ true } />
            { isFilesEdit ? <Select
               title={ data.value?.isLoading ? 'Loading Documents...' : 'Document' }
               data={ documentSelectData }
               handleSelectItem={ usesGlobalSpreadsheet ? () => {} : handleSelectDoc }
               isSmall={ true }
               emptyMsg="Fetch documents to choose one"
            /> : null }
            { data.value?.request === 'create_sheet' ? <TextInput keyRef="sheet_name" title="Sheet" placeholder="new_sheet_name" tooltip="The name for the new tab you're creating in this spreadsheet." value={ data.value?.sheet_name || '' } handleChange={ handleChangeInput } />
            : !['create_spreadsheet', 'delete_spreadsheet'].includes(data.value?.request) ? <Select
               title={ isLoadingSheets ? 'Loading Sheets...' : 'Sheet' }
               data={ sheets }
               handleSelectItem={ handleSelectSheet }
               isSmall={ true }
               emptyMsg={ effectiveSpreadsheetId ? 'No sheets found' : 'Choose a document first' }
            /> : null }
            { !['delete_spreadsheet', 'clear_sheet', 'create_sheet', 'delete_sheet'].includes(data.value?.request) ? <TextInput
               keyRef="any" // @ts-ignore
               title={ inputAny[data.value?.request]?.title } // @ts-ignore
               placeholder={ inputAny[data.value?.request]?.placeholder } // @ts-ignore
               tooltip={ inputAny[data.value?.request]?.tooltip }
               value={ data.value?.any || '' }
               handleChange={ handleChangeInput }
            /> : null }
            { ['upsert_row_value', 'update_row', 'update_multi_row', 'get_row'].includes(data.value?.request) ? <TextInput keyRef="search_value" title="Search" placeholder={ data.value?.request === 'get_row' ? 'value_to_search (Optional)' : 'search_value (Required)' } tooltip={ data.value?.request === 'get_row' ? 'Optional — only return rows that contain this value somewhere in the row.' : 'The value to search for in your sheet (e.g. a phone number or ID) to find the matching row.' } value={ data.value?.search_value } handleChange={ handleChangeInput } /> : null }
            { ['upsert_row_value', 'update_multi_row', 'delete_columns', 'delete_rows', 'get_column', 'get_row'].includes(data.value?.request) ? <TextInput
                  keyRef="end_range"
                  title={ ['upsert_row_value', 'update_multi_row'].includes(data.value?.request) ? 'Start' : 'End' } // @ts-ignore
                  placeholder={ inputAny[data.value?.request]?.endRange } // @ts-ignore
                  tooltip={ inputAny[data.value?.request]?.endRangeTooltip }
                  value={ data.value?.end_range }
                  handleChange={ handleChangeInput }
               /> : null }
            { ['append_row', 'append_multi_row', 'upsert_row_value', 'update_row', 'update_multi_row'].includes(data.value?.request) ?
               <Textarea
                  keyRef="area"
                  title="Data" // @ts-ignore
                  value={ data.value?.area } // @ts-ignore
                  placeholder={ inputAny[data.value?.request]?.area } // @ts-ignore
                  tooltip={ inputAny[data.value?.request]?.areaTooltip }
                  handleChange={ (e: any, keyRef?: string) => updateNodeValue(id, setNodes, { [keyRef as string]: e.target.value }) }
               />
            : null }
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
const inputAny = {
   create_spreadsheet: { title: 'Title', placeholder: 'title_spreadsheet (Required)', tooltip: 'The name to give the new Google Sheet file.' },
   get_row: {
      title: 'Start', placeholder: 'row_number', endRange: 'row_number',
      tooltip: 'Optional row number to start reading from. Leave empty to read the whole sheet.',
      endRangeTooltip: 'Optional row number to stop reading at.'
   },
   get_column: {
      title: 'Start', placeholder: 'column_letter', endRange: 'column_letter',
      tooltip: 'The column letter to read (e.g. A).',
      endRangeTooltip: 'Optional column letter to stop reading at, to read a range of columns.'
   },
   upsert_row_value: {
      title: 'Column', placeholder: 'column_search_letter (Default A)',
      area: 'Your input should look like: \n["column1 value", "column2 value"]',
      endRange: 'column_start_add (Optional)',
      tooltip: 'The column letter to search in to find the row (default A). Create this as a column in your sheet — e.g. put phone numbers or IDs in column A.',
      endRangeTooltip: 'The column letter to write the new value(s) into once the row is found. Leave empty to write starting from the search column.',
      areaTooltip: 'The value(s) to write, in column order. If the search value is found the row is updated, otherwise a new row is appended. Create one sheet column for each value you list here, in the same order.'
   },
   append_row: {
      title: 'Column', placeholder: 'column_start_letter (Default A)', area: 'Your input should look like: \n["column1 value", "column2 value"]',
      tooltip: 'The column letter where the new row starts (default A).',
      areaTooltip: 'The values for the new row, in order — the first value goes under the column above, the next under the following column, and so on. Create a column header in your sheet for each value, in this same order.'
   },
   append_multi_row: {
      title: 'Column', placeholder: 'column_start_letter (Default A)', area: '[\n["column1 value", "column2 value"],\n["column1 value", "column2 value"]\n]',
      tooltip: 'The column letter where each new row starts (default A).',
      areaTooltip: 'A list of rows to append, each row written as a list of values in column order. Create one sheet column per value.'
   },
   update_row: {
      title: 'Column', placeholder: 'column_search_letter (Default A)', area: 'Your input should look like: \n["column1 value", "column2 value"]',
      tooltip: 'The column letter to search in to find the row to update (default A).',
      areaTooltip: 'The new values to write into that row, starting from the search column, in order.'
   },
   update_multi_row: {
      title: 'Column', placeholder: 'column_search_letter (Default A)',
      area: '[\n["column1 value", "column2 value"],\n["column1 value", "column2 value"]\n]',
      endRange: 'column_start_add (Optional)',
      tooltip: 'The column letter to search in to find each row to update (default A).',
      endRangeTooltip: 'The column letter to start writing the new values into. Leave empty to write starting from the search column.',
      areaTooltip: 'A list of rows to update, each written as a list of values in column order.'
   },
   delete_columns: {
      title: 'Start', placeholder: 'column_letter', endRange: 'column_letter',
      tooltip: 'The first column letter to delete.',
      endRangeTooltip: 'The last column letter to delete.'
   },
   delete_rows: {
      title: 'Start', placeholder: 'row_number', endRange: 'row_number',
      tooltip: 'The first row number to delete.',
      endRangeTooltip: 'The last row number to delete (exclusive).'
   }
};